import { useEffect, useRef } from 'react'
import { useGuideContext } from '@/contexts/GuideContext'

interface PendingGuideOptions {
  /** Selector CSS que debe existir en el DOM antes de arrancar el tour. */
  anchorSelector: string
  /** Tiempo máximo de espera en ms antes de arrancar igual. Default 3000. */
  timeoutMs?: number
}

/**
 * Llama a start() cuando:
 * 1. GuideContext.pendingGuideId coincide con guideId, Y
 * 2. el elemento anchorSelector aparece en el DOM (polling vía rAF).
 *
 * Si el anchor no aparece dentro de timeoutMs, arranca de todos modos para que
 * al menos el popover de apertura centrado (element: undefined) sea visible.
 *
 * Mecanismo: requestAnimationFrame poll (no setInterval, no MutationObserver).
 * rAF es barato, está atado al ciclo de pintado del browser y se cancela
 * limpiamente en el cleanup del useEffect.
 *
 * La guarda firedRef evita disparos múltiples por re-renders entre el
 * momento en que pendingGuideId se setea y el momento en que clearPendingGuide
 * limpia el estado (análogo a autoStartFiredRef en useTour.ts).
 */
export function usePendingGuide(
  guideId: string,
  start: () => void,
  { anchorSelector, timeoutMs = 3000 }: PendingGuideOptions,
): void {
  const { pendingGuideId, clearPendingGuide } = useGuideContext()
  const firedRef = useRef(false)

  useEffect(() => {
    if (pendingGuideId !== guideId) return
    if (firedRef.current) return

    let rafId = 0
    const deadline = performance.now() + timeoutMs

    const tick = () => {
      if (document.querySelector(anchorSelector) || performance.now() >= deadline) {
        firedRef.current = true
        clearPendingGuide()
        start()
        return
      }
      rafId = requestAnimationFrame(tick)
    }

    rafId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafId)
  }, [pendingGuideId, guideId, anchorSelector, timeoutMs, start, clearPendingGuide])
}
